import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getBrandDetails } from '../services/brand-service';
import { refreshToken } from '../services/login-service';
import { RootState, persistor, store } from '../store';
import { revertAll } from '../store/actions';
import { setBrandDetails } from '../store/reducers/brandSlice';
import { updateLoginStatus, updateLoginUserDetails } from '../store/reducers/userSlice';
import { clearStoragePreservingMigrationCookies } from '../utils/migration-cookies';
import { NavigationConfig } from '../utils/navigation-constants';

/**
 * Hook to validate session on app load
 * Checks if stored auth tokens are still valid by attempting to refresh the token
 * If validation fails, clears auth state and redirects to login
 */
export const useSessionValidation = () => {
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
    const refreshTokenValue = useSelector((state: RootState) => state.user.refreshToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const validationAttemptedRef = useRef(false);
    // Validate only if session already existed on initial app load.
    // This prevents an unnecessary refresh immediately after login/verify-otp.
    const shouldValidateOnLoadRef = useRef(Boolean(isLoggedIn && refreshTokenValue));

    useEffect(() => {
        // Only validate once per app load
        if (validationAttemptedRef.current) {
            return;
        }

        // Skip validation when auth was established after initial render (fresh login flow).
        if (!shouldValidateOnLoadRef.current) {
            return;
        }

        // Only validate if user is logged in with a refresh token
        if (!isLoggedIn || !refreshTokenValue) {
            return;
        }

        validationAttemptedRef.current = true;

        const validateSession = async () => {
            try {
                // Try to refresh the token to verify it's still valid
                const response = await refreshToken(refreshTokenValue, false); // Don't show loader

                if (response?.statusCode === 200 || response?.data?.token) {
                    // Session is valid, update tokens
                    const currentState = store.getState() as RootState;
                    const currentUserDetails = currentState.user.userDetails;
                    const newRefreshToken = response.data?.refreshToken || response?.refreshToken || refreshTokenValue;

                    dispatch(
                        updateLoginUserDetails({
                            token: response.data?.token || response.data?.accessToken || response.accessToken,
                            refreshToken: newRefreshToken,
                            user: currentUserDetails ?? {},
                        }),
                    );
                } else {
                    // Token refresh failed - session is invalid
                    handleInvalidSession();
                }
            } catch (error) {
                console.error('Session validation error:', error);
                // If validation fails, treat as invalid session
                handleInvalidSession();
            }
        };

        const handleInvalidSession = () => {
            // Clear auth state
            dispatch(revertAll());
            dispatch(updateLoginStatus(false));

            // Clear persisted state
            persistor.purge();

            // Clear localStorage
            clearStoragePreservingMigrationCookies();

            // Redirect to login
            navigate(`${NavigationConfig.Login}?source=login`);

            // Fetch fresh brand details after logout. Rejection is handled by the chained
            // .catch(), so no surrounding try/catch is needed.
            getBrandDetails(false)
                .then(response => {
                    if (response.statusCode === 200 && response.data) {
                        dispatch(setBrandDetails(response.data));
                    }
                })
                .catch(error => {
                    console.error('Error fetching brand details:', error);
                });
        };

        validateSession();
    }, [isLoggedIn, refreshTokenValue, dispatch, navigate]);
};
